import { format } from 'date-fns'
import type { ErrorEvent, ErrorEventsResponse } from '@/types/forklift'
import { getErrorDescription, getErrorTitle } from '@/common/errorDescriptions'
import { getScenarioLabel } from '@/common/scenarioLabel'

// メトリクス画面「エラー別集計」の CSV (エラー発生 1 件 = 1 行)。
// 1 行目: ロボット ID と集計期間、2 行目: 列ヘッダ、3 行目以降: 発生ごとの行。
// 時刻は閲覧環境のローカル時刻 (日本時間での運用を想定)。
// ジャーナル対応前の日付の行 (30分バケットから再構成) は回復時刻が空になる。
// 複数機体をまとめて出す場合は No の次に「ロボットID」列を足し、機体ごとのブロックで並べる
export const ERROR_EVENTS_CSV_COLUMNS = [
  'No',
  '年月日',
  '発生時刻',
  '回復時刻',
  '継続時間（秒）',
  '回復の記録',
  'エラーコード',
  'エラー名',
  'レベル',
  'メッセージ（機体）',
  '発生時のシナリオID',
  '発生時のシナリオ名',
  '起動元',
] as const

const LEVEL_LABELS: Record<number, string> = {
  0: 'INFO',
  1: 'WARN',
  2: 'ERROR',
}

// 回復時刻が空の行の理由を示す
const RECOVERY_LABELS: Record<string, string> = {
  recovered: '回復',
  bridge_restart: '機体再起動のため不明',
  open: '期間内に未回復',
  unknown: '未対応バージョンのため不明',
}

const TRIGGER_LABELS: Record<string, string> = {
  ui: 'UI',
  api: 'API',
  plc: 'PLC',
  command: '指令',
  unknown: '単発',
}

function escapeCsv(v: string | number): string {
  const s = String(v)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function formatSlashDate(yyyymmdd: string): string {
  return yyyymmdd.replace(/-/g, '/')
}

function toSec(ms: number | null): string {
  if (ms == null) return ''
  return String(Math.round(ms / 1000))
}

function errorName(code: number): string {
  const description = getErrorDescription(code)
  return description.startsWith('未定義') ? '' : getErrorTitle(description)
}

function scenarioName(
  ev: ErrorEvent,
  nameById: Record<string, string>
): string {
  if (ev.run_kind === 'task') return '単発実行'
  if (!ev.scenario_id && !ev.scenario_name) return ''
  if (ev.scenario_name) {
    return getScenarioLabel({ id: ev.scenario_id, name: ev.scenario_name })
  }
  return (
    nameById[ev.scenario_id] ??
    getScenarioLabel({ id: ev.scenario_id, name: null })
  )
}

function eventToRow(
  no: number,
  ev: ErrorEvent,
  nameById: Record<string, string>
): (string | number)[] {
  const ts = new Date(ev.ts)
  return [
    no,
    format(ts, 'yyyy/MM/dd'),
    format(ts, 'HH:mm:ss'),
    ev.recovered_ts ? format(new Date(ev.recovered_ts), 'HH:mm:ss') : '',
    toSec(ev.downtime_ms),
    RECOVERY_LABELS[ev.recovery] ?? ev.recovery,
    ev.code,
    errorName(ev.code),
    ev.level != null ? (LEVEL_LABELS[ev.level] ?? String(ev.level)) : '',
    ev.message,
    ev.run_kind === 'task' ? '' : ev.scenario_id,
    scenarioName(ev, nameById),
    ev.trigger ? (TRIGGER_LABELS[ev.trigger] ?? ev.trigger) : '',
  ]
}

function toCsvText(lines: (string | number)[][]): string {
  return '﻿' + lines.map((row) => row.map(escapeCsv).join(',')).join('\r\n')
}

export function buildErrorEventsCsv(
  data: ErrorEventsResponse,
  nameById: Record<string, string>
): string {
  const title = `ロボットID: ${data.fork_id}, 期間: ${formatSlashDate(
    data.period.start
  )} - ${formatSlashDate(data.period.end)}`
  const rows = data.events.map((ev, i) => eventToRow(i + 1, ev, nameById))
  return toCsvText([[title], [...ERROR_EVENTS_CSV_COLUMNS], ...rows])
}

// 複数機体分を 1 ファイルにまとめる。1 行目にロボット ID をすべて列挙し、
// 各行の 2 列目にロボット ID を入れる。行は機体ごとにまとめて発生順
export function buildMultiErrorEventsCsv(
  list: ErrorEventsResponse[],
  nameById: Record<string, string>
): string {
  if (list.length === 1) return buildErrorEventsCsv(list[0], nameById)
  const period = list[0]?.period ?? { start: '', end: '' }
  const title = `ロボットID: ${list
    .map((d) => d.fork_id)
    .join(' / ')}, 期間: ${formatSlashDate(period.start)} - ${formatSlashDate(
    period.end
  )}`
  const [no, ...restColumns] = ERROR_EVENTS_CSV_COLUMNS
  const header = [no, 'ロボットID', ...restColumns]
  const rows = list.flatMap((data) =>
    data.events.map((ev, i) => {
      const [rowNo, ...rest] = eventToRow(i + 1, ev, nameById)
      return [rowNo, data.fork_id, ...rest]
    })
  )
  return toCsvText([[title], header, ...rows])
}
